import React, { useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, onValue, query, orderByChild } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { PET_SIZES, PET_TYPES } from '../constants';

const AdoptedPetCard = ({ pet }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === pet.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? pet.images.length - 1 : prevIndex - 1
    );
  };

  const handleViewDetails = () => {
    navigate(`/pet/${pet.id}`);
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden transform transition duration-500 hover:scale-105">
      <div className="relative h-64 cursor-pointer" onClick={handleViewDetails}>
        <img
          src={pet.images[currentImageIndex]}
          alt={`${pet.name} - ${pet.type === 'dog' ? 'Perro' : 'Gato'} adoptado`}
          className="w-full h-full object-cover"
        />
        <button 
          onClick={(e) => {
            e.stopPropagation();
            prevImage();
          }} 
          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md"
        >
          &#8249;
        </button>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            nextImage();
          }} 
          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md"
        >
          &#8250;
        </button>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent text-white p-4">
          <h2 className="text-xl font-bold">
            {pet.name} - {PET_TYPES[pet.type]?.label}
          </h2>
        </div>
      </div>
      <div className="p-4">
        <p className="text-sm text-gray-600 mb-2">
          {PET_SIZES[pet.size]?.label} • {pet.color} • {pet.location}
        </p>
        <p className="text-gray-700 mb-4 line-clamp-3">{pet.description}</p>
        <div className="flex justify-between items-center">
          <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
            Adoptado
          </span>
          <button
            onClick={handleViewDetails}
            className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded"
          >
            Ver historia
          </button>
        </div>
      </div>
    </div>
  );
};

const AdoptedPetsList = () => {
  const [adoptedPets, setAdoptedPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    size: ''
  });

  useEffect(() => {
    const petsRef = query(ref(database, 'pets'), orderByChild('status'));
  
    const unsubscribe = onValue(petsRef, (snapshot) => {
      const petsData = [];
      
      snapshot.forEach((childSnapshot) => {
        const pet = { id: childSnapshot.key, ...childSnapshot.val() };
        if (pet.status === "adopt") {
          petsData.push(pet);
        }
      });

      setAdoptedPets(petsData.reverse());  // Reverse para mostrar los más nuevos primero
      setLoading(false);
    }, (error) => {
      console.error("Error fetching adopted pets: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const filteredPets = adoptedPets.filter(pet => {
    const matchesFilter = Object.entries(filters).every(([key, value]) => {
      return value === '' || pet[key] === value;
    });
    const matchesSearch = searchTerm === '' || 
                          pet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          pet.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({ ...prevFilters, [name]: value }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Historias de Éxito</h1>
        <div className="bg-green-100 text-green-800 py-3 px-6 rounded-lg inline-flex items-center">
          <span className="text-4xl font-bold mr-2">{adoptedPets.length}</span>
          <span className="text-xl">mascotas han encontrado un hogar feliz</span>
        </div>
      </div>
      
      <div className="mb-6">
        <input
          type="text"
          placeholder="Buscar mascotas adoptadas..."
          className="w-full p-2 border rounded"
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      <div className="mb-6 flex flex-wrap gap-4">
        <select
          name="type"
          onChange={handleFilterChange}
          className="p-2 border rounded"
        >
          <option value="">Todos los tipos</option>
          {Object.values(PET_TYPES).map(type => (
            <option key={type.value} value={type.value}>
              {type.icon} {type.label}
            </option>
          ))}
        </select>
        <select
          name="size"
          onChange={handleFilterChange}
          className="p-2 border rounded"
        >
          <option value="">Todos los tamaños</option>
          {Object.values(PET_SIZES).map(size => (
            <option key={size.value} value={size.value}>
              {size.label}
            </option>
          ))}
        </select>
      </div>

      {filteredPets.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 text-xl">No se encontraron mascotas adoptadas que coincidan con los criterios de búsqueda.</p>
          {adoptedPets.length > 0 && (
            <p className="text-gray-600 mt-2">Prueba ajustando los filtros para ver resultados.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPets.map((pet) => (
            <AdoptedPetCard key={pet.id} pet={pet} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdoptedPetsList;